import { blockGroups } from "../flowBuilderConstants";
import { getBlockMeta } from "../flowBuilderUtils";
import type { CustomerConfig, Environment, VariableRow } from "../types";

type Props = {
  customersLoading: boolean;
  customersError?: string | null;
  selectedCustomer?: CustomerConfig | null;
  environment: Environment;
  setEnvironment: React.Dispatch<React.SetStateAction<Environment>>;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customerPickerOpen: boolean;
  setCustomerPickerOpen: (value: boolean) => void;
  filteredCustomers: CustomerConfig[];
  selectCustomer: (customer: CustomerConfig) => void;
  payloadIsValid: boolean;
  payloadVariables: VariableRow[];
  addBlock: (block: string) => void;
  openPayloadEditor: () => void;
};

export default function FlowBuilderSidebar({
  selectedCustomer,
  environment,
  setEnvironment,
  customerSearch,
  setCustomerSearch,
  filteredCustomers,
  selectCustomer,
  payloadIsValid,
  payloadVariables,
  addBlock,
  openPayloadEditor,
}: Props) {
  return (
    <aside className="flex w-[340px] shrink-0 flex-col border-r border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
      <div className="border-b border-slate-200 p-5 dark:border-white/10">
        <div className="text-xs font-black uppercase tracking-[0.22em] text-orange-500">
          JDX Foundry
        </div>
        <div className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white">
          Flow Builder
        </div>
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          Bouw een integratieflow met trigger, klantcontext, mapping en output.
        </p>
      </div>

      <div className="flex-1 space-y-5 overflow-auto p-5">
        <section>
          <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            Klant
          </div>

          <input
            value={customerSearch}
            onChange={(event) => setCustomerSearch(event.target.value)}
            placeholder="Zoek klant..."
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-orange-400 focus:bg-white dark:border-white/10 dark:bg-white/[0.04] dark:text-white"
          />

          <div className="mt-2 max-h-44 space-y-2 overflow-auto">
            {filteredCustomers.slice(0, 8).map((customer) => {
              const selected = selectedCustomer?.customerId === customer.customerId;

              return (
                <button
                  key={customer.customerKey}
                  type="button"
                  onClick={() => selectCustomer(customer)}
                  className={`w-full rounded-xl border px-3 py-2 text-left transition ${
                    selected
                      ? "border-orange-300 bg-orange-50 dark:border-orange-500/30 dark:bg-orange-500/10"
                      : "border-slate-200 bg-slate-50 hover:border-orange-200 hover:bg-orange-50 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-orange-500/10"
                  }`}
                >
                  <div className="truncate text-sm font-black text-slate-950 dark:text-white">
                    {customer.customerName}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                    {customer.customerId}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            Omgeving
          </div>

          <div className="grid grid-cols-3 gap-2">
            {(["dev", "acc", "prod"] as const).map((env) => (
              <button
                key={env}
                type="button"
                onClick={() => setEnvironment(env)}
                className={`rounded-xl border px-3 py-2 text-xs font-black uppercase transition ${
                  environment === env
                    ? "border-orange-300 bg-orange-500 text-white"
                    : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-orange-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300"
                }`}
              >
                {env}
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            Input payload
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-white p-3 dark:bg-slate-950/60">
              <div className="text-[10px] font-black uppercase text-slate-400">
                Status
              </div>
              <div
                className={`mt-1 text-sm font-black ${
                  payloadIsValid ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {payloadIsValid ? "Geldig" : "Ongeldig"}
              </div>
            </div>

            <div className="rounded-xl bg-white p-3 dark:bg-slate-950/60">
              <div className="text-[10px] font-black uppercase text-slate-400">
                Velden
              </div>
              <div className="mt-1 text-sm font-black text-slate-950 dark:text-white">
                {payloadVariables.length}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={openPayloadEditor}
            className="mt-3 w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-400"
          >
            Payload invoeren
          </button>
        </section>

        <section>
          <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            Blocks
          </div>

          <div className="space-y-3">
            {blockGroups.map((group) => (
              <details key={group.title} open={group.title === "JDX Blocks"}>
                <summary className="cursor-pointer rounded-xl bg-slate-100 px-3 py-2 text-xs font-black uppercase tracking-[0.13em] text-slate-500 dark:bg-white/[0.05] dark:text-slate-400">
                  {group.title}
                </summary>

                <div className="mt-2 space-y-2">
                  {group.blocks.map((block) => {
                    const meta = getBlockMeta(block);

                    return (
                      <button
                        key={block}
                        type="button"
                        onClick={() => addBlock(block)}
                        className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-orange-300 hover:bg-orange-50 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-orange-500/10"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-xs font-black text-white">
                          {meta.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-black text-slate-950 dark:text-white">
                            {block}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {meta.type}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </aside>
  );
}